export const SCHOOL_CATEGORIES = {
    // Selected School Categories
    CONTINUOUS_STABLE: 'CONTINUOUS_STABLE',
    CONTINUOUS_INCREASING: 'CONTINUOUS_INCREASING',
    CONTINUOUS_DECREASING: 'CONTINUOUS_DECREASING',
    NEW: 'NEW',
    NEWER_INCREASING: 'NEWER_INCREASING',
    NEWER_DECREASING: 'NEWER_DECREASING',
    NEWER_STABLE: 'NEWER_STABLE',
    
    // Nearby School Categories
    CLOSED: 'CLOSED',
    NEW_OR_NEWER: 'NEW_OR_NEWER',
    UNKNOWN: 'UNKNOWN'
  };
  
  export const countGradesWithEnrollment = (enrollmentData) => {
    return Object.values(enrollmentData || {}).filter(enrollment => enrollment > 0).length;
  };
  
  export const hasDataInYear = (enrollmentData) => {
    return Object.values(enrollmentData || {}).some(enrollment => enrollment > 0);
  };
  
  /**
   * A school reporting no students in the current year is treated as closed.
   *
   * Deliberately reads the whole current bucket rather than the grades a user has
   * selected: a school still enrolling students in grades outside that selection has
   * not closed. The directory's grades-offered row is not consulted, because it keeps
   * the values from the school's last active year and so outlives the school.
   */
  export const isClosedByEnrollment = (enrollmentByGrade) =>
    !hasDataInYear(enrollmentByGrade?.current);
  
  export const categorizeSelectedSchool = (schoolData) => {
    const { enrollment_by_grade } = schoolData;
    
    // Check if school has comparison data (2019-20 school year, data_year 2019)
    const hasComparisonData = hasDataInYear(enrollment_by_grade.comparison);
    const has2024Data = hasDataInYear(enrollment_by_grade.current);
    
    if (hasComparisonData) {
      const gradesComparison = countGradesWithEnrollment(enrollment_by_grade.comparison);
      const grades2024 = countGradesWithEnrollment(enrollment_by_grade.current);
      
      if (grades2024 > gradesComparison) {
        return SCHOOL_CATEGORIES.CONTINUOUS_INCREASING;
      } else if (grades2024 < gradesComparison) {
        return SCHOOL_CATEGORIES.CONTINUOUS_DECREASING;
      }
      return SCHOOL_CATEGORIES.CONTINUOUS_STABLE;
    }
    
    // Check intermediate years (2020-2023 data years for 2020-21 to 2023-24 school years).
    // A school with no 2019-20 record but data in one of these opened after the baseline.
    const intermediateYears = Object.keys(enrollment_by_grade)
      .filter(year => !isNaN(parseInt(year)) && parseInt(year) >= 2020 && parseInt(year) <= 2023)
      .sort();
      
    if (intermediateYears.length > 0) {
      const firstYear = intermediateYears[0];
      const initialGrades = countGradesWithEnrollment(enrollment_by_grade[firstYear]);
      const currentGrades = countGradesWithEnrollment(enrollment_by_grade.current);
      
      if (currentGrades > initialGrades) {
        return SCHOOL_CATEGORIES.NEWER_INCREASING;
      } else if (currentGrades < initialGrades) {
        return SCHOOL_CATEGORIES.NEWER_DECREASING;
      }
      return SCHOOL_CATEGORIES.NEWER_STABLE;
    }
    
    // If only has current year data
    if (has2024Data) {
      return SCHOOL_CATEGORIES.NEW;
    }
    
    return SCHOOL_CATEGORIES.UNKNOWN;
  };
  
  export const categorizeNearbySchool = (schoolData) => {
    const { enrollment_by_grade } = schoolData;
    
    const hasComparisonData = hasDataInYear(enrollment_by_grade.comparison);
    const has2024Data = !isClosedByEnrollment(enrollment_by_grade);
    
    // Handle closed schools
    if (hasComparisonData && !has2024Data) {
      return SCHOOL_CATEGORIES.CLOSED;
    }
    
    // Handle new/newer schools
    if (!hasComparisonData && has2024Data) {
      return SCHOOL_CATEGORIES.NEW_OR_NEWER;
    }
    
    // Handle continuous schools
    if (hasComparisonData && has2024Data) {
      const gradesComparison = countGradesWithEnrollment(enrollment_by_grade.comparison);
      const grades2024 = countGradesWithEnrollment(enrollment_by_grade.current);
      
      if (grades2024 > gradesComparison) {
        return SCHOOL_CATEGORIES.CONTINUOUS_INCREASING;
      } else if (grades2024 < gradesComparison) {
        return SCHOOL_CATEGORIES.CONTINUOUS_DECREASING;
      }
      return SCHOOL_CATEGORIES.CONTINUOUS_STABLE;
    }
    
    return SCHOOL_CATEGORIES.UNKNOWN;
  };
  
  export const categorizeSchools = (schoolsData, selectedSchoolId) => {
    return schoolsData.map(schoolData => {
      const category = schoolData.uuid === selectedSchoolId
        ? categorizeSelectedSchool(schoolData)
        : categorizeNearbySchool(schoolData);
  
      return {
        ...schoolData,
        category
      };
    });
  };